import type { MtbTrailMetadata, UpdatePlaceData } from "~/lib/api";
import { MtbTrailCategoryBadge, MtbTrailCategorySelector } from "./MtbTrailCategorySelector";
import { MtbTrailDifficultyBadge, MtbTrailDifficultySelector } from "./MtbTrailDifficultySelector";
import { DistanceDisplay, DistanceEditor } from "~/components/place/DistanceEditor";
import { ElevationDisplay, ElevationEditor } from "~/components/place/ElevationEditor";
import { InfoField } from "~/components/place/InfoField";
import { StatsLine } from "~/components/place/StatsLine";
import { TrackUploader } from "~/components/place/TrackUploader";
import { PanelSection } from "~/components/ui/panel-section";

interface MtbTrailInfoSectionProps {
  metadata: MtbTrailMetadata | null;
  canEdit?: boolean;
  onUpdate?: (data: UpdatePlaceData) => Promise<void>;
  track?: GeoJSON.LineString | null;
  onTrackUpload?: (file: File) => Promise<void>;
  onTrackDelete?: () => Promise<void>;
}

export function MtbTrailInfoSection({
  metadata,
  canEdit,
  onUpdate,
  track,
  onTrackUpload,
  onTrackDelete,
}: MtbTrailInfoSectionProps) {
  const { difficulty, category, distance, elevation } = metadata ?? {};

  const hasElevation = elevation && (elevation.gain > 0 || elevation.loss > 0);
  const hasContent = !!(difficulty || category || distance || hasElevation || track);

  if (!canEdit && !hasContent) return null;

  const updateMetadata = (updates: Partial<MtbTrailMetadata>) => {
    onUpdate?.({ metadata: { ...metadata, ...updates } });
  };

  return (
    <>
      <PanelSection.Separator />
      <PanelSection.Root>
        <PanelSection.Title>Trail Info</PanelSection.Title>
        <PanelSection.Content className="space-y-3">
          {canEdit ? (
            <>
              {onTrackUpload && <TrackUploader track={track} onUpload={onTrackUpload} onDelete={onTrackDelete} />}
              <InfoField label="Difficulty">
                <MtbTrailDifficultySelector
                  value={difficulty ?? null}
                  onChange={(d) => updateMetadata({ difficulty: d ?? undefined })}
                />
              </InfoField>
              <InfoField label="Category">
                <MtbTrailCategorySelector
                  value={category ?? null}
                  onChange={(c) => updateMetadata({ category: c ?? undefined })}
                />
              </InfoField>
              <InfoField label="Distance">
                <DistanceEditor
                  value={distance}
                  onChange={(d) => updateMetadata({ distance: d ?? undefined })}
                  canEdit
                />
              </InfoField>
              <InfoField label="Elevation">
                <ElevationEditor
                  value={elevation}
                  onChange={(e) => updateMetadata({ elevation: e ?? undefined })}
                  canEdit
                />
              </InfoField>
            </>
          ) : (
            <StatsLine className="text-muted-foreground">
              {difficulty && <MtbTrailDifficultyBadge difficulty={difficulty} />}
              {category && <MtbTrailCategoryBadge category={category} />}
              {distance && <DistanceDisplay distance={distance} />}
              {hasElevation && <ElevationDisplay elevation={elevation} />}
            </StatsLine>
          )}
        </PanelSection.Content>
      </PanelSection.Root>
    </>
  );
}
